import React, { useCallback, useMemo } from 'react';
import {
  EndDateConfigKind,
  IConnectionInput,
  IDataSourceInput,
  IEndDateConfigInput,
} from '../../../../utils/apollo/graphqlSchema';
import DataModelerConnectionInput from './DataModelerConnectionInput';
import DataModelerEndDateInput from './DataModelerEndDateInput';
import { useAppDispatch } from '../../../../redux/hooks';
import {
  dataModelerUpdateDataSource,
  dataModelerUpdateEndDateConfig,
} from '../../../../redux/slices/dataModeler';
import {
  dataSourceMetaToInput,
  emptyDataSourceInput,
  endDateMetaToInput,
} from '../../../../redux/slices/dataModeler/utils';
import { canEditConnectionDetails } from './utils';
import { DataSourceForm, DataSourceTab } from './DataModelerDataSourceInputUI';
import DataModelerTestConnection from './DataModelerTestConnection';
import { useDataModelerContext } from '../../DataModelerContext';

const emptyEndDateInput = (): IEndDateConfigInput => {
  return {
    kind: EndDateConfigKind.Current,
    rollingCount: null,
    rollingTimeUnit: null,
    startTimeUnit: null,
    staticDate: null,
    staticDateAsLiveStart: true,
  };
};

const DataModelerDataSourceInput = () => {
  const dispatch = useAppDispatch();
  const { draftEdits, schemaObj, isEditMode } = useDataModelerContext();
  const draftDataSource: IDataSourceInput = useMemo(
    () =>
      draftEdits?.dataSource ??
      (schemaObj?.dsConfig
        ? dataSourceMetaToInput(schemaObj.dsConfig)
        : emptyDataSourceInput()),
    [draftEdits?.dataSource, schemaObj?.dsConfig],
  );
  const draftEndDate: IEndDateConfigInput = useMemo(
    () =>
      draftEdits?.endDate ??
      (schemaObj?.dsConfig?.endDate
        ? endDateMetaToInput(schemaObj.dsConfig.endDate)
        : emptyEndDateInput()),
    [draftEdits?.endDate, schemaObj?.dsConfig.endDate],
  );

  const setDataSource = useCallback(
    (dataSourceInput: IDataSourceInput | null) => {
      dispatch(dataModelerUpdateDataSource(dataSourceInput));
    },
    [dispatch],
  );

  const setConnection = useCallback(
    (connection: IConnectionInput) => {
      setDataSource({
        ...draftDataSource,
        connection,
      });
    },
    [draftDataSource, setDataSource],
  );

  const setDateDt = useCallback(
    (newDateDt: string) => {
      const dateDt = newDateDt === '' ? null : newDateDt;
      setDataSource({
        ...draftDataSource,
        dateDt,
      });
    },
    [draftDataSource, setDataSource],
  );

  const setEndDateConfig = useCallback(
    (endDate: IEndDateConfigInput) => {
      dispatch(dataModelerUpdateEndDateConfig(endDate));
    },
    [dispatch],
  );

  const isNewSchema = draftEdits?.isNew ?? false;
  const canEditConnection = canEditConnectionDetails(
    isNewSchema,
    !isEditMode,
    draftDataSource.connection,
  );
  return (
    <DataSourceTab>
      <DataSourceForm>
        <DataModelerConnectionInput
          isNewSchema={isNewSchema}
          readOnly={!isEditMode}
          connection={draftDataSource.connection}
          onChange={setConnection}
          dateDt={draftDataSource.dateDt ?? ''}
          setDateDt={setDateDt}
        />
        <DataModelerEndDateInput
          readOnly={!isEditMode}
          endDate={draftEndDate}
          onChange={setEndDateConfig}
        />
      </DataSourceForm>
      <DataModelerTestConnection
        readOnly={!canEditConnection}
        connection={draftDataSource.connection}
      />
    </DataSourceTab>
  );
};

export default DataModelerDataSourceInput;
